// Target-state planner: turns a scan (current SKUs/seats) + chosen moves into delta cost, feature gains/losses and retirable add-ons.
import { parseCell } from "./matrix";

const isGranted = (raw) => ["check", "text"].includes(parseCell(raw).kind);
const hasCell = (row, planId) => !!(planId && isGranted(row.cells?.[planId]));

export const priceFor = (plan, mode) => (plan?.price ? plan.price[plan.addon ? "teams" : mode] : null);

/** Feature rows granted by a bundle of matrix plan ids (base + suite add-ons). */
export const grantedRows = (rows, planIds) => rows.filter((r) => r.type === "feature" && planIds.some((id) => hasCell(r, id)));

const rowKey = (r) => r.id;

export const featureDiff = (rows, fromIds, toIds) => {
  const from = new Set(grantedRows(rows, fromIds).map(rowKey));
  const to = new Set(grantedRows(rows, toIds).map(rowKey));
  const gains = rows.filter((r) => to.has(r.id) && !from.has(r.id));
  const losses = rows.filter((r) => from.has(r.id) && !to.has(r.id));
  return { gains, losses };
};

export const groupBySection = (list) => {
  const out = {};
  list.forEach((r) => {
    (out[r.sectionKey] = out[r.sectionKey] || []).push(r);
  });
  return out;
};

/** Standalone add-ons the planner can stack on a base plan (name in the add-on price list -> matrix feature rows it grants). */
export const EXTRA_ADDONS = {
  "Entra ID Plan 1 (standalone)": ["Entra ID Plan 1"],
  "Entra ID Plan 2 Step-up": ["Entra ID Plan 1", "Entra ID Plan 2"],
  "Intune Plan 1": ["Intune Plan 1"],
};
export const UNLICENSED_SKU = "__unlicensed__";

export const extraRows = (rows, extras, fromPlan) => {
  const names = new Set(extras.flatMap((n) => EXTRA_ADDONS[n] || []).map((n) => n.toLowerCase()));
  return rows.filter((r) => r.type === "feature" && names.has(r.name.toLowerCase()) && !hasCell(r, fromPlan));
};

export const defaultMoves = (summary) =>
  summary.skus
    .filter((s) => s.kind === "base" && s.plan_id && s.consumed > 0)
    .map((s) => ({ from_sku: s.part_number, from_plan: s.plan_id, seats: s.consumed, to_plan: null, to_addons: [], to_extras: [] }));

/** Merge pre-filled moves (e.g. gap remediation) into the current list; unknown SKUs (unlicensed users) are appended. */
export const mergeMoves = (current, prefill) => {
  const out = current.map((m) => prefill.find((p) => p.from_sku === m.from_sku) || m);
  prefill.forEach((p) => {
    if (!out.some((m) => m.from_sku === p.from_sku)) out.push(p);
  });
  return out;
};

/** Add-on price by matrix add-on product name (case-insensitive). */
const addonPrice = (addons, name) => {
  const a = addons.find((x) => x.name.toLowerCase() === (name || "").toLowerCase());
  return a ? { price: a.price, estimated: !!a.estimated } : { price: null, estimated: false };
};

export const computePlan = ({ summary, moves, plans, addons, rows, priceMode }) => {
  const planById = Object.fromEntries(plans.map((p) => [p.id, p]));
  const skuByPart = Object.fromEntries(summary.skus.map((s) => [s.part_number, s]));
  const skuById = Object.fromEntries(summary.skus.map((s) => [s.sku_id, s]));

  const unitOf = (planId, noTeams) => priceFor(planById[planId], noTeams ? "noTeams" : priceMode);

  const lines = moves.map((m) => {
    const sku = skuByPart[m.from_sku];
    const fromPlan = planById[m.from_plan];
    const extras = m.to_extras || [];
    const unlicensed = !m.from_plan;
    const targetBase = m.to_plan || m.from_plan;
    const changed = !!m.to_plan || m.to_addons.length > 0 || extras.length > 0;
    const seats = Math.min(Math.max(m.seats || 0, 0), sku?.consumed ?? m.seats);
    const currentUnit = unlicensed ? 0 : unitOf(m.from_plan, sku?.no_teams);
    const baseUnit = targetBase ? unitOf(targetBase, sku?.no_teams && targetBase === m.from_plan) : 0;
    const addonUnits = [
      ...m.to_addons.map((id) => ({ id, name: planById[id]?.short || id, price: priceFor(planById[id], priceMode), estimated: !!planById[id]?.estimated })),
      ...extras.map((name) => ({ id: name, name, extra: true, ...addonPrice(addons, name) })),
    ];
    const targetUnit = [baseUnit, ...addonUnits.map((a) => a.price)].some((v) => v === null || v === undefined) ? null : baseUnit + addonUnits.reduce((s, a) => s + a.price, 0);
    const diff = changed ? featureDiff(rows, unlicensed ? [] : [m.from_plan], [targetBase, ...m.to_addons].filter(Boolean)) : { gains: [], losses: [] };
    if (changed && extras.length) {
      const have = new Set(diff.gains.map((r) => r.id));
      extraRows(rows, extras, m.from_plan).forEach((r) => {
        if (!have.has(r.id)) diff.gains.push(r);
      });
    }

    // standalone add-ons held by these users that the target now includes
    const retire = [];
    if (changed && sku?.co_assigned) {
      const targetIds = [targetBase, ...m.to_addons];
      Object.entries(sku.co_assigned).forEach(([otherId, count]) => {
        const other = skuById[otherId];
        if (!other || !other.feature_row) return;
        const row = rows.find((r) => r.type === "feature" && r.name.toLowerCase() === other.feature_row.toLowerCase());
        const alreadyIncluded = row && hasCell(row, m.from_plan);
        if (!row || alreadyIncluded || !targetIds.some((id) => hasCell(row, id))) return;
        const share = sku.consumed ? Math.round((count * seats) / sku.consumed) : count;
        const { price, estimated } = addonPrice(addons, other.addon_name);
        retire.push({ sku: other, seats: share, unit: price, estimated, monthly: price === null ? null : price * share, feature_row: other.feature_row });
      });
    }
    const currentMonthly = currentUnit === null ? null : currentUnit * seats;
    const targetMonthly = targetUnit === null ? null : targetUnit * seats;
    const retireMonthly = retire.reduce((s, r) => s + (r.monthly || 0), 0);
    return {
      ...m,
      to_extras: extras,
      sku,
      fromPlan,
      unlicensed,
      targetBase: planById[targetBase],
      changed,
      seats,
      currentUnit,
      baseUnit,
      addonUnits,
      targetUnit,
      estimated: !!(planById[targetBase]?.estimated || addonUnits.some((a) => a.estimated) || fromPlan?.estimated),
      currentMonthly,
      targetMonthly,
      retire,
      retireMonthly,
      licenseDeltaMonthly: currentMonthly === null || targetMonthly === null ? null : targetMonthly - currentMonthly,
      deltaMonthly: currentMonthly === null || targetMonthly === null ? null : targetMonthly - currentMonthly - retireMonthly,
      gains: diff.gains,
      losses: diff.losses,
    };
  });

  // whole-tenant baseline: every consumed SKU we can price (base, suite, add-on)
  const baseline = summary.skus
    .filter((s) => s.consumed > 0 && s.kind !== "free")
    .map((s) => {
      let unit = null;
      let estimated = false;
      if (s.plan_id && planById[s.plan_id]) {
        unit = unitOf(s.plan_id, s.no_teams);
        estimated = !!planById[s.plan_id].estimated;
      } else if (s.addon_name) {
        ({ price: unit, estimated } = addonPrice(addons, s.addon_name));
      }
      return { sku: s, unit, estimated, monthly: unit === null ? null : unit * s.consumed };
    });
  const currentMonthly = baseline.reduce((s, b) => s + (b.monthly || 0), 0);
  const unpriced = baseline.filter((b) => b.monthly === null);
  const deltaMonthly = lines.reduce((s, l) => s + (l.deltaMonthly || 0), 0);
  const retireMonthly = lines.reduce((s, l) => s + l.retireMonthly, 0);
  const gains = lines.reduce((s, l) => s + l.gains.length, 0);
  const losses = lines.reduce((s, l) => s + l.losses.length, 0);
  return {
    lines,
    baseline,
    unpriced,
    currentMonthly,
    targetMonthly: currentMonthly + deltaMonthly,
    deltaMonthly,
    retireMonthly,
    gains,
    losses,
    estimated: lines.some((l) => l.changed && l.estimated) || baseline.some((b) => b.estimated && b.monthly),
  };
};

/** "E5 add-on math": E3 + Defender Suite + Purview Suite vs E5 (and the reverse). */
export const e5Math = ({ line, plans, rows, priceMode }) => {
  const byId = Object.fromEntries(plans.map((p) => [p.id, p]));
  const target = line.to_plan || line.from_plan;
  const addons = new Set(line.to_addons);
  const both = addons.has("entdef") && addons.has("entpur");
  let bundle;
  let alt;
  if (target === "m365e3" && both) {
    bundle = { ids: ["m365e3", "entdef", "entpur"], label: "E3 + Defender Suite + Purview Suite" };
    alt = { ids: ["m365e5"], label: "Microsoft 365 E5" };
  } else if (target === "m365e5" && line.changed) {
    bundle = { ids: ["m365e5"], label: "Microsoft 365 E5" };
    alt = { ids: ["m365e3", "entdef", "entpur"], label: "E3 + Defender Suite + Purview Suite" };
  } else {
    return null;
  }
  const unit = (ids) => (ids.some((id) => priceFor(byId[id], priceMode) === null) ? null : ids.reduce((s, id) => s + priceFor(byId[id], priceMode), 0));
  const bundleUnit = unit(bundle.ids);
  const altUnit = unit(alt.ids);
  const diff = featureDiff(rows, bundle.ids, alt.ids);
  return {
    bundle: { ...bundle, unit: bundleUnit, monthly: bundleUnit === null ? null : bundleUnit * line.seats },
    alt: { ...alt, unit: altUnit, monthly: altUnit === null ? null : altUnit * line.seats },
    altOnly: diff.gains,
    bundleOnly: diff.losses,
    cheaper: bundleUnit === null || altUnit === null ? null : altUnit < bundleUnit ? "alt" : altUnit > bundleUnit ? "bundle" : "same",
  };
};
